import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { LabelPreview } from "@/components/LabelPreview";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";

export default function Settings() {
  const [form, setForm] = useState({
    shop_name: "", address: "", postal_code: "", city: "", country: "France",
    phone: "", email: "", siret: "", logo_base64: "",
    label_width_mm: 60, label_height_mm: 40, show_pictograms: true, logo_size_percent: 25,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm((f) => ({ ...f, ...data })));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Logo trop volumineux (max 500 Ko)"); return; }
    const reader = new FileReader();
    reader.onload = () => upd("logo_base64", reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        ...form,
        label_width_mm: Number(form.label_width_mm) || 60,
        label_height_mm: Number(form.label_height_mm) || 40,
        logo_size_percent: Number(form.logo_size_percent) || 25,
      });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Paramètres boutique">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-zinc-800">
        {/* Shop coords */}
        <div className="lg:col-span-6 p-6 border-b lg:border-b-0 lg:border-r border-zinc-800 space-y-4">
          <SectionTitle title="Coordonnées de la boutique" hint="Apparaissent sur chaque étiquette" />
          <F label="Nom de la boutique"><Input data-testid="settings-shop-name" className="rounded-none bg-zinc-900 border-zinc-800" value={form.shop_name || ""} onChange={(e) => upd("shop_name", e.target.value)} /></F>
          <F label="Adresse"><Input data-testid="settings-address" className="rounded-none bg-zinc-900 border-zinc-800" value={form.address || ""} onChange={(e) => upd("address", e.target.value)} /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Code postal"><Input data-testid="settings-postal" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.postal_code || ""} onChange={(e) => upd("postal_code", e.target.value)} /></F>
            <F label="Ville"><Input data-testid="settings-city" className="rounded-none bg-zinc-900 border-zinc-800" value={form.city || ""} onChange={(e) => upd("city", e.target.value)} /></F>
            <F label="Pays"><Input data-testid="settings-country" className="rounded-none bg-zinc-900 border-zinc-800" value={form.country || ""} onChange={(e) => upd("country", e.target.value)} /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Téléphone"><Input data-testid="settings-phone" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} /></F>
            <F label="Email"><Input data-testid="settings-email" className="rounded-none bg-zinc-900 border-zinc-800" value={form.email || ""} onChange={(e) => upd("email", e.target.value)} /></F>
          </div>
          <F label="SIRET"><Input data-testid="settings-siret" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.siret || ""} onChange={(e) => upd("siret", e.target.value)} /></F>
        </div>

        {/* Label format + logo */}
        <div className="lg:col-span-6 p-6 space-y-4">
          <SectionTitle title="Format & logo de l'étiquette" hint="Le PDF s'adapte à ces dimensions" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Largeur (mm)"><Input data-testid="settings-width" type="number" min="20" max="200" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.label_width_mm} onChange={(e) => upd("label_width_mm", e.target.value)} /></F>
            <F label="Hauteur (mm)"><Input data-testid="settings-height" type="number" min="15" max="200" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.label_height_mm} onChange={(e) => upd("label_height_mm", e.target.value)} /></F>
          </div>
          <OrientationToggle
            width={Number(form.label_width_mm) || 60}
            height={Number(form.label_height_mm) || 40}
            onRotate={() => setForm((f) => ({ ...f, label_width_mm: f.label_height_mm, label_height_mm: f.label_width_mm }))}
          />
          <div className="flex items-center justify-between border border-zinc-800 p-4">
            <div>
              <div className="text-sm font-medium text-white">Afficher les pictogrammes</div>
              <div className="text-xs text-zinc-500 mt-1">-18 ans + femmes enceintes</div>
            </div>
            <Switch data-testid="settings-pictograms" checked={!!form.show_pictograms} onCheckedChange={(v) => upd("show_pictograms", v)} />
          </div>
          <F label="Logo de la boutique">
            <label className="block border border-dashed border-zinc-700 hover:border-red-500/60 p-6 cursor-pointer transition-colors" data-testid="settings-logo-drop">
              <div className="flex items-center gap-4">
                {form.logo_base64 ? (
                  <img src={form.logo_base64} alt="logo" className="w-16 h-16 object-contain bg-white p-1" />
                ) : (
                  <div className="w-16 h-16 bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500">
                    <Upload className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 text-sm text-zinc-400">Cliquez pour uploader un logo (max 500 Ko)</div>
                {form.logo_base64 && (
                  <button data-testid="settings-logo-remove" onClick={(e) => { e.preventDefault(); upd("logo_base64", ""); }} className="text-xs text-red-500 hover:text-red-400">Supprimer</button>
                )}
              </div>
              <input data-testid="settings-logo-input" type="file" accept="image/*" onChange={onLogo} className="hidden" />
            </label>
          </F>

          {form.logo_base64 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500">
                  Taille du logo sur l'étiquette
                </label>
                <span className="text-xs font-mono text-white" data-testid="settings-logo-size-value">
                  {form.logo_size_percent}%
                </span>
              </div>
              <Slider
                data-testid="settings-logo-size"
                min={10} max={60} step={1}
                value={[form.logo_size_percent || 25]}
                onValueChange={(v) => upd("logo_size_percent", v[0])}
                className="[&_[role=slider]]:bg-red-500 [&_[role=slider]]:border-red-500"
              />
              <div className="text-[10px] font-mono text-zinc-500 mt-2">
                Emplacement dédié en haut à droite — n'empiète jamais sur les informations texte.
              </div>
            </div>
          )}

          {/* Live label preview */}
          <div className="pt-2 border-t border-zinc-800">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
              Aperçu réel · {form.label_width_mm}×{form.label_height_mm} mm
            </div>
            <div className="bg-zinc-900/40 border border-dashed border-zinc-800 p-4 flex items-center justify-center">
              <LabelPreview data={{
                shop_name: form.shop_name || "—",
                shop_address: [form.address, form.postal_code, form.city, form.country].filter(Boolean).join(", "),
                label_width_mm: Number(form.label_width_mm) || 60,
                label_height_mm: Number(form.label_height_mm) || 40,
                show_pictograms: form.show_pictograms,
                logo_base64: form.logo_base64,
                logo_size_percent: Number(form.logo_size_percent) || 25,
                brand: "Alfaliquid",
                flavor: "Fraise Menthe",
                ufi: "XXXX-YYYY-ZZZZ-1234",
                batch_number: "L260917-001",
                dluo_date: "03/28",
                bottle_size_ml: 50,
                nicotine_mg: 3,
              }} scale={3.5} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-red-500 hover:bg-red-600 rounded-none px-8">
          {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
        </Button>
      </div>

      <div className="mt-8 border border-zinc-800 p-6 max-w-2xl">
        <div className="font-display text-lg font-bold tracking-tight">Sécurité — Changer mon mot de passe</div>
        <div className="text-xs text-zinc-500 mt-1 mb-4">
          Toute autre session ouverte sera déconnectée après changement.
        </div>
        <ChangePasswordForm />
      </div>
    </DashboardLayout>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (next.length < 6) { toast.error("Minimum 6 caractères"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      if (data.token) localStorage.setItem("vape_token", data.token);
      toast.success("Mot de passe changé");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Mot de passe actuel</label>
        <Input data-testid="pwd-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
          className="rounded-none bg-zinc-900 border-zinc-800" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Nouveau (min. 6 car.)</label>
          <Input data-testid="pwd-new" type="password" minLength={6} required value={next} onChange={(e) => setNext(e.target.value)}
            className="rounded-none bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Confirmer</label>
          <Input data-testid="pwd-confirm" type="password" minLength={6} required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="rounded-none bg-zinc-900 border-zinc-800" />
        </div>
      </div>
      <Button data-testid="pwd-submit" disabled={loading} className="bg-red-500 hover:bg-red-600 rounded-none">
        {loading ? "Enregistrement…" : "Changer mon mot de passe"}
      </Button>
    </form>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
function SectionTitle({ title, hint }) {
  return (
    <div className="mb-2">
      <div className="font-display text-lg font-bold tracking-tight">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}

function OrientationToggle({ width, height, onRotate }) {
  const isLandscape = width >= height;
  const orientation = isLandscape ? "Paysage" : "Portrait";
  // Mini visual: keep the container size stable, scale the inner rect to reflect orientation ratio
  const maxDim = 44;
  const ratio = isLandscape ? height / width : width / height;
  const wPx = isLandscape ? maxDim : maxDim * ratio;
  const hPx = isLandscape ? maxDim * ratio : maxDim;
  return (
    <div className="flex items-center justify-between border border-zinc-800 p-4" data-testid="orientation-toggle">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-zinc-900 border border-zinc-800 flex items-center justify-center">
          <div
            className="bg-red-500/20 border border-red-500 transition-all"
            style={{ width: `${wPx}px`, height: `${hPx}px` }}
          />
        </div>
        <div>
          <div className="text-sm font-medium text-white flex items-center gap-2" data-testid="orientation-label">
            Orientation · <span className="text-red-400">{orientation}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 font-mono">
            {width} × {height} mm
          </div>
        </div>
      </div>
      <Button
        data-testid="rotate-btn"
        type="button"
        onClick={onRotate}
        variant="outline"
        className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white"
      >
        <RotateCcw className="w-4 h-4 mr-2" /> Rotation 90°
      </Button>
    </div>
  );
}

